import React from 'react';

import { Stack } from '@mui/material';

import {
  useGetDashboardCvRevenueByVerificationTypeQuery,
  useGetDashboardPlatformAdminFinancialDataQuery,
  useGetDashboardPlatformAdminRevenueDistributionsQuery,
} from 'src/api';
import { DEFAULT_END_DATE, DEFAULT_START_DATE } from 'src/constants/dashboard';
import { useDashboardCVAdminData, useDashboardPrintView } from 'src/hooks/dashboard';

import { DashboardCVAdminFinancialDataBlock, DashboardFilterPreviewPrint } from './components';
import { DashboardClearVerifyRevenueByVerificationType } from './components/DashboardClearVerifyRevenueByVerificationType';
import { DashboardClearVerifyRevenueShare } from './components/DashboardClearVerifyRevenueShare';

export const DashboardFinancialDataAccess: React.FC = () => {
  const {
    date,
    peos,
    employers,
    revenueByVerificationTypeStartDate,
    revenueByVerificationTypeEndDate,
    revenueDistributionsStartDate,
    revenueDistributionsEndDate,
  } = useDashboardCVAdminData();
  const { isPrintView } = useDashboardPrintView();

  const {
    data: { getDashboardCVRevenueByVerificationType } = {},
    loading: loadingRevenueByVerificationType,
  } = useGetDashboardCvRevenueByVerificationTypeQuery({
    variables: {
      peoIds: peos?.map(peo => peo.id),
      employerIds: employers?.map(employer => employer.id),
      from: revenueByVerificationTypeStartDate?.toISODate() || DEFAULT_START_DATE.toISODate(),
      to: revenueByVerificationTypeEndDate?.toISODate() || DEFAULT_END_DATE.toISODate(),
    },
  });

  const {
    data: { getDashboardPlatformAdminRevenueDistributions } = {},
    loading: loadingRevenueDistribution,
  } = useGetDashboardPlatformAdminRevenueDistributionsQuery({
    variables: {
      peoIds: peos?.map(peo => peo.id),
      employerIds: employers?.map(employer => employer.id),
      from: revenueDistributionsStartDate?.toISO() || DEFAULT_START_DATE.toISO(),
      to: revenueDistributionsEndDate?.toISO() || DEFAULT_END_DATE.toISO(),
    },
  });

  const {
    data: { getDashboardPlatformAdminFinancialData: financialData } = {},
    loading: loadingCVAdminFinancialData,
  } = useGetDashboardPlatformAdminFinancialDataQuery({
    variables: {
      asOfDate: date.endOf('day').toISO(),
      peoIds: peos?.map(peo => peo.id),
      employerIds: employers?.map(employer => employer.id),
    },
  });

  return (
    <Stack spacing={3} width="100%">
      <DashboardCVAdminFinancialDataBlock
        asOfDate={date}
        loading={loadingCVAdminFinancialData}
        financialData={financialData}
      />
      <DashboardClearVerifyRevenueByVerificationType
        revenueByVerificationTypeItems={getDashboardCVRevenueByVerificationType ?? []}
        loading={loadingRevenueByVerificationType}
      />
      <DashboardClearVerifyRevenueShare
        revenueDistributions={getDashboardPlatformAdminRevenueDistributions ?? []}
        loading={loadingRevenueDistribution}
      />
      {isPrintView && <DashboardFilterPreviewPrint />}
    </Stack>
  );
};
